import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import privateAPI from '../redux/privateApi';
import { useCallback } from "react";
import axios from 'axios';


const SelectedPhotos = () => {
  const [photoData, setPhotoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState({});
  const [savingSelections, setSavingSelections] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'large'
  const navigate = useNavigate();
  
  // New states for full-size view modal
  const [fullSizeModalOpen, setFullSizeModalOpen] = useState(false);
  const [currentFullSizePhoto, setCurrentFullSizePhoto] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100); // percentage
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const imageRef = useRef(null);
  
  // New state for submit confirmation popup
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [userId , setUserId] = useState("");

useEffect(() => {
  const queryParams = new URLSearchParams(window.location.search);
  const urlUserId = queryParams.get('user_id');
  let finalUserId = urlUserId || localStorage.getItem('user_id');

  //console.log("Current URL:", window.location.href);

  if (urlUserId) {
    localStorage.setItem('user_id', urlUserId);
    //console.log('Using user_id from URL:', urlUserId);
  } else if (finalUserId) {
    //console.log('Using user_id from localStorage:', finalUserId);
  } else {
    //console.error("No user_id found in URL or localStorage.");
    setError("User ID missing. Cannot load photos.");
    setLoading(false);
    return;
  }

  const fetchPhotos = async () => {
    try {
      setLoading(true);

      //console.log("Fetching with user_id:", finalUserId);

      const response = await fetch(
        `https://backend.newbrindha.com/photostudio/admin/private/get_files?user_id=${finalUserId}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const processed = data.uploaded_files || [];
      const uniquePhotos = new Map();

      processed.forEach((item) => {
        const uniqueKey = `${item.category}-${item.private_files_id}-${item.file_url}`;
        if (!uniquePhotos.has(uniqueKey)) {
          const fileName = extractFilenameFromUrl(item.file_url);
          uniquePhotos.set(uniqueKey, {
            ...item,
            uniqueKey,
            fileName: fileName
          });
        }
      });

      setPhotoData(Array.from(uniquePhotos.values()));
      setSelectedPhotos({});
      setLoading(false);
    } catch (err) {
      //console.error('Error fetching photos:', err);
      setError(err.message || 'Failed to fetch photos');
      setLoading(false);
    }
  };

  // ✅ use finalUserId directly here
  if (finalUserId) {
    fetchPhotos();
  }
}, []);





  const handleLogout = useCallback(() => {
    navigate("/loginprivate");
  }, [navigate]);


  // Add keyboard event listeners for the full-size modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!fullSizeModalOpen) return;
      
      const categorizedPhotos = processPhotosByCategory();
      const currentCategory = currentFullSizePhoto ? currentFullSizePhoto.category : null;
      
      if (!currentCategory || !categorizedPhotos[currentCategory]) return;
      
      const categoryPhotos = categorizedPhotos[currentCategory];
      const currentIndex = categoryPhotos.findIndex(photo => 
        photo.uniqueKey === currentFullSizePhoto.uniqueKey
      );
      
      switch (e.key) {
        case 'Escape':
          setFullSizeModalOpen(false);
          break;
        case 'ArrowRight':
          if (currentIndex < categoryPhotos.length - 1) {
            setCurrentFullSizePhoto(categoryPhotos[currentIndex + 1]);
            resetZoomAndPosition();
          }
          break;
        case 'ArrowLeft':
          if (currentIndex > 0) {
            setCurrentFullSizePhoto(categoryPhotos[currentIndex - 1]);
            resetZoomAndPosition();
          }
          break;
        case '+':
        case '=':
          handleZoom(Math.min(zoomLevel + 25, 300));
          break;
        case '-':
          handleZoom(Math.max(zoomLevel - 25, 50));
          break;
        case '0':
          resetZoomAndPosition();
          break;
        case ' ':
          handleSelectPhoto(currentFullSizePhoto.uniqueKey);
          e.preventDefault();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullSizeModalOpen, currentFullSizePhoto, zoomLevel]);
  
  // Extract filename from URL
  const extractFilenameFromUrl = (url) => {
    if (!url) return "Unknown";
    
    // Split by slash and get the last part
    const parts = url.split('/');
    let fileName = parts[parts.length - 1];
    
    // Remove query parameters if any
    if (fileName.includes('?')) {
      fileName = fileName.split('?')[0];
    }
    
    // Decode URI components to handle special characters
    try {
      fileName = decodeURIComponent(fileName);
    } catch (e) {
      //console.error("Error decoding filename:", e);
    }
    
    // Truncate if too long (for display purposes)
    if (fileName.length > 25) {
      const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
      const name = fileName.substring(0, 20);
      fileName = extension ? `${name}...${extension}` : `${name}...`;
    }
    
    return fileName;
  };
  
  // Process photos by category
  const processPhotosByCategory = () => {
    const categories = {};
    
    photoData.forEach(item => {
      const category = item.category;
      
      // Initialize category array if it doesn't exist
      if (!categories[category]) {
        categories[category] = [];
      }
      
      categories[category].push({
        id: item.id,
        url: item.file_url,
        uploadedAt: item.uploaded_at,
        fileType: item.file_type,
        privateFilesId: item.private_files_id,
        userId: item.user_id,
        uniqueKey: item.uniqueKey,
        fileName: item.fileName || extractFilenameFromUrl(item.file_url),
        category: item.category
      });
    });
    
    return categories;
  };

  // Function to handle checkbox changes
  const handleSelectPhoto = (uniqueKey) => {
    setSelectedPhotos(prev => {
      const updatedSelection = { ...prev };
      
      // Toggle selection using uniqueKey
      if (updatedSelection[uniqueKey]) {
        // If already selected, remove it
        delete updatedSelection[uniqueKey];
      } else {
        // If not selected, add it
        updatedSelection[uniqueKey] = true;
      }
      
      return updatedSelection;
    });
  };

  // Check if a photo is selected using uniqueKey
  const isPhotoSelected = (uniqueKey) => {
    return !!selectedPhotos[uniqueKey];
  };

  // Function to save selected photos
 
const saveSelectedPhotos = async () => {
  try {
    setSavingSelections(true);

    // Get user ID dynamically
    const user_id = localStorage.getItem('user_id');


    if (!user_id) {
      throw new Error('User ID not found. Please log in again.');
    }

    // Get selected photo uniqueKeys
    const selectedItems = Object.keys(selectedPhotos).filter(
      (key) => selectedPhotos[key]
    );

    // Group selections by private_files_id
    const selectionsByPrivateFile = {};
    selectedItems.forEach((uniqueKey) => {
      const photo = photoData.find((p) => p.uniqueKey === uniqueKey);
      if (photo) {
        const { private_files_id, file_url } = photo;
        if (!selectionsByPrivateFile[private_files_id]) {
          selectionsByPrivateFile[private_files_id] = [];
        }
        selectionsByPrivateFile[private_files_id].push(file_url);
      }
    });

    // Format the payload
    const privateFilesData = Object.entries(selectionsByPrivateFile).map(
      ([privateFilesId, selected_urls]) => ({
        private_files_id: parseInt(privateFilesId),
        selected_urls,
      })
    );

    // Send API request using axios
    await axios.post(
      'https://backend.newbrindha.com/photostudio/user/private/select-files',
      {
        user_id,
        private_files: privateFilesData,
      }
    );

    // Success handling
    setSaveSuccess(true);
    setShowSubmitPopup(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setShowSubmitPopup(false);
    }, 3000);
  } catch (err) {
    //console.error('Error saving selected photos:', err);
    setError(err.message || 'Failed to save selections.');
  } finally {
    setSavingSelections(false);
  }
};

  

  // Function to view selected photos
  const viewSelectedPhotos = () => {
    navigate('/getSelected');
  };

  // Toggle view mode between grid and large
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'grid' ? 'large' : 'grid');
  };

  // Count total selected photos
  const countTotalSelectedPhotos = () => {
    return Object.keys(selectedPhotos).filter(key => selectedPhotos[key]).length;
  };

  // New functions for full-size view modal
  const openFullSizeModal = (photo) => {
    setCurrentFullSizePhoto(photo);
    resetZoomAndPosition();
    setFullSizeModalOpen(true);
    
    // Add overflow hidden to body to prevent scrolling
    document.body.style.overflow = 'hidden';
  };

  const closeFullSizeModal = () => {
    setFullSizeModalOpen(false);
    setCurrentFullSizePhoto(null);
    resetZoomAndPosition();
    
    // Restore body overflow
    document.body.style.overflow = 'auto';
  };

  const resetZoomAndPosition = () => {
    setZoomLevel(100);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoom = (newZoomLevel) => {
    setZoomLevel(newZoomLevel);
  };

  // Handle mouse events for dragging
  const handleMouseDown = (e) => {
    if (zoomLevel > 100) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 100) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      setPosition({
        x: newX,
        y: newY
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Navigate to next/previous photo in full-size view
  const navigateToAdjacentPhoto = (direction) => {
    if (!currentFullSizePhoto) return;
    
    const categorizedPhotos = processPhotosByCategory();
    const currentCategory = currentFullSizePhoto.category;
    
    if (!categorizedPhotos[currentCategory]) return;
    
    const categoryPhotos = categorizedPhotos[currentCategory];
    const currentIndex = categoryPhotos.findIndex(photo => 
      photo.uniqueKey === currentFullSizePhoto.uniqueKey
    );
    
    if (direction === 'next' && currentIndex < categoryPhotos.length - 1) {
      setCurrentFullSizePhoto(categoryPhotos[currentIndex + 1]);
      resetZoomAndPosition();
    } else if (direction === 'prev' && currentIndex > 0) {
      setCurrentFullSizePhoto(categoryPhotos[currentIndex - 1]);
      resetZoomAndPosition();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-lg text-purple-800 font-medium">Loading your photos...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-red-50 to-pink-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Photos</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors shadow-md hover:shadow-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  const categorizedPhotos = processPhotosByCategory();
  const totalSelected = countTotalSelectedPhotos();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-2 sm:p-4 pt-16 sm:pt-20">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-4 sm:mb-8 bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 md:p-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Your Photo Selection
                </h1>
                <p className="text-sm sm:text-base text-gray-500 mt-1">
                  Select your favorite photos from each category
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2 sm:gap-3 w-full md:w-auto">
                <button
                  onClick={toggleViewMode}
                  className="px-3 sm:px-4 py-2 sm:py-3 bg-white border border-gray-200 text-gray-700 rounded-lg sm:rounded-xl 
                    font-medium hover:bg-gray-50 transition-all shadow-sm hover:shadow flex items-center justify-center"
                >
                  {viewMode === 'grid' ? (
                    <>
                      <svg className="w-4 h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8v.01M4 16v.01M8 4h.01M16 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="hidden xs:inline">Large View</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      <span className="hidden xs:inline">Grid View</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={saveSelectedPhotos}
                  disabled={savingSelections || totalSelected === 0}
                  className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all flex items-center justify-center 
                    ${savingSelections ? 'bg-indigo-400' : totalSelected === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'} 
                    text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5`}
                >
                  {savingSelections ? (
                    <>
                      <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
                      </svg>
                      <span className="whitespace-nowrap">Submit {totalSelected > 0 && `(${totalSelected})`}</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={viewSelectedPhotos}
                  className="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg sm:rounded-xl 
                    font-medium hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg 
                    transform hover:-translate-y-0.5 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  <span className="whitespace-nowrap">View Selected</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg sm:rounded-xl 
                    font-medium hover:from-red-600 hover:to-rose-700 transition-all shadow-md hover:shadow-lg 
                    transform hover:-translate-y-0.5 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12h4"></path>
                  </svg>
                  <span className="whitespace-nowrap">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-green-100 to-emerald-100 border-l-4 border-emerald-500 
            text-emerald-700 rounded-lg flex items-center animate-fade-in-down">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <div>
              <p className="font-medium">Success!</p>
              <p className="text-xs sm:text-sm">Your selections have been saved successfully.</p>
            </div>
          </div>
        )}
        
        {/* Photos Grid */}
        {Object.keys(categorizedPhotos).length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-lg text-center">
            <div className="max-w-md mx-auto">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <h3 className="text-lg sm:text-xl font-medium text-gray-700 mb-2">No photos available</h3>
              <p className="text-sm sm:text-base text-gray-500">There are currently no photos available for selection.</p>
            </div>
          </div>
        ) : (
          Object.keys(categorizedPhotos).map((category) => (
            <div 
              key={category} 
              className="mb-6 sm:mb-10 bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
            >
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                    <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                      {category}
                    </span>
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full">
                      {categorizedPhotos[category].length} items
                    </span>
                    <span className="text-xs sm:text-sm text-purple-500 bg-purple-100 px-2 sm:px-3 py-1 rounded-full">
                      {categorizedPhotos[category].filter(photo => isPhotoSelected(photo.uniqueKey)).length} selected
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-3 sm:p-6">
                {viewMode === 'grid' ? (
                  // Grid View
                  (<div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5">
                    {categorizedPhotos[category].map((photo) => (
                      <div 
                        key={photo.uniqueKey} 
                        className={`relative group overflow-hidden rounded-lg sm:rounded-xl transition-all duration-300 
                          ${isPhotoSelected(photo.uniqueKey) ? 'ring-2 ring-purple-500 ring-offset-2' : ''}
                          hover:shadow-lg hover:transform hover:-translate-y-1`}
                      >
                        <div className="aspect-w-1 aspect-h-1 overflow-hidden bg-gray-100 rounded-lg sm:rounded-xl cursor-pointer">
                          {photo.fileType.startsWith('image/') ? (
                            <img
                              src={photo.url}
                              alt={photo.fileName}
                              className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                              loading="lazy"
                              onClick={() => handleSelectPhoto(photo.uniqueKey)}
                            />
                          ) : (
                            <div 
                              className="flex items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400"
                              onClick={() => handleSelectPhoto(photo.uniqueKey)}
                            >
                              <svg className="w-8 h-8 sm:w-12 sm:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                              </svg>
                            </div>
                          )}
                          
                          {/* Filename overlay */}
                          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent 
                            py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <p className="text-white text-xs sm:text-sm font-medium truncate">
                              {photo.fileName}
                            </p>
                          </div>

                          {/* Checkbox for selection */}
<div className="absolute top-2 right-2 z-10">
  <div 
    className={`w-5 h-5 sm:w-6 sm:h-6 border-2 rounded-full cursor-pointer flex items-center justify-center
      ${isPhotoSelected(photo.uniqueKey) 
        ? 'bg-purple-500 border-white' 
        : 'bg-white/70 border-gray-300 hover:bg-white'}`}
    onClick={(e) => {
      e.stopPropagation();
      handleSelectPhoto(photo.uniqueKey);
    }}
  >
    {isPhotoSelected(photo.uniqueKey) && (
      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
      </svg>
    )}
  </div>
</div>

{/* Full-size view button */}
<div className="absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
  <button 
    onClick={(e) => {
      e.stopPropagation();
      openFullSizeModal(photo);
    }}
    className="w-5 h-5 sm:w-6 sm:h-6 bg-white/70 hover:bg-white rounded-full flex items-center justify-center shadow-sm"
    title="View full size"
  >
    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
    </svg>
  </button>
</div>
</div>
                      </div>
                    ))}
                  </div>)
                ) : (
                  // Large View
                  (<div className="grid grid-cols-1 gap-4 sm:gap-6">
                    {categorizedPhotos[category].map((photo) => (
                      <div 
                        key={photo.uniqueKey} 
                        className={`relative group overflow-hidden rounded-lg sm:rounded-xl transition-all duration-300 
                          bg-gray-50 border ${isPhotoSelected(photo.uniqueKey) ? 'border-purple-300 bg-purple-50/50' : 'border-gray-100'}
                          hover:shadow-lg`}
                      >
                        <div className="p-3 sm:p-4 flex flex-col sm:flex-row gap-4">
                          {/* Image preview */}
                          <div 
                            className="flex-shrink-0 w-full sm:w-48 md:w-60 h-48 sm:h-36 md:h-40 overflow-hidden rounded-lg bg-gray-100 cursor-pointer"
                            onClick={() => openFullSizeModal(photo)}
                          >
                            {photo.fileType.startsWith('image/') ? (
                              <img
                                src={photo.url}
                                alt={photo.fileName}
                                className="object-cover w-full h-full hover:scale-105 transition-transform duration-500"
                                loading="lazy"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400">
                                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          {/* Information */}
                          <div className="flex-grow flex flex-col justify-between py-1">
                            <div>
                              <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-1 sm:mb-2 break-all">
                                {photo.fileName}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-500 mb-2">
                                {photo.fileType.replace('image/', '').toUpperCase()}
                              </p>
                            </div>
                            
                            <div className="flex items-center justify-between mt-auto">
                              <div className="text-xs text-gray-500">
                                {new Date(photo.uploadedAt).toLocaleDateString()}
                              </div>
                              
                              <div className="flex gap-2">
                                <button
                                  onClick={() => openFullSizeModal(photo)}
                                  className="px-3 py-1 sm:px-4 sm:py-2 bg-gray-100 text-gray-700 rounded-lg text-xs sm:text-sm font-medium 
                                    hover:bg-gray-200 transition-colors flex items-center gap-1 sm:gap-2"
                                >
                                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                  </svg>
                                  View
                                </button>
                                
                                <button
                                  onClick={() => handleSelectPhoto(photo.uniqueKey)}
                                  className={`px-3 py-1 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium 
                                    flex items-center gap-1 sm:gap-2 transition-colors
                                    ${isPhotoSelected(photo.uniqueKey) 
                                      ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                                      : 'bg-indigo-500 text-white hover:bg-indigo-600'}`}
                                >
                                  {isPhotoSelected(photo.uniqueKey) ? (
                                    <>
                                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                      </svg>
                                      Selected
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                                      </svg>
                                      Select
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>)
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {/* Full-Size Image Modal */}
      {fullSizeModalOpen && currentFullSizePhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 overflow-hidden"
          ref={modalRef}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="absolute inset-0 flex flex-col">
            {/* Header Controls */}
            <div className="p-4 flex justify-between items-center bg-gradient-to-b from-black/40 to-transparent">
              <div className="flex items-center space-x-4">
                {/* <button
                  onClick={resetZoomAndPosition}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-lg p-2 transition-colors"
                  title="Reset zoom"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button> */}
                
                <div className="flex items-center space-x-2 bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => handleZoom(Math.max(zoomLevel - 25, 50))}
                    className="text-white hover:bg-white/20 rounded p-1 transition-colors"
                    disabled={zoomLevel <= 50}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                    </svg>
                  </button>
                  
                  <span className="text-white text-sm px-2">{zoomLevel}%</span>
                  
                  <button
                    onClick={() => handleZoom(Math.min(zoomLevel + 25, 300))}
                    className="text-white hover:bg-white/20 rounded p-1 transition-colors"
                    disabled={zoomLevel >= 300}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
                
                <button
                  onClick={() => handleSelectPhoto(currentFullSizePhoto.uniqueKey)}
                  className={`rounded-lg p-2 transition-colors flex items-center space-x-2
                    ${isPhotoSelected(currentFullSizePhoto.uniqueKey) 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                      : 'bg-white/10 hover:bg-white/20 text-white'}`}
                >
                  {isPhotoSelected(currentFullSizePhoto.uniqueKey) ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="hidden sm:inline">Selected</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="hidden sm:inline">Select</span>
                    </>
                  )}
                </button>
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-white text-sm hidden sm:inline">
                  {currentFullSizePhoto.fileName}
                </span>
                
                <button
                  onClick={closeFullSizeModal}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-lg p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Image Container */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden">
              {/* Navigation buttons */}
              <button
                onClick={() => navigateToAdjacentPhoto('prev')}
                className="absolute left-4 z-10 bg-black/30 hover:bg-black/50 rounded-full p-2 text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Image */}
              <div 
                className="relative cursor-grab active:cursor-grabbing" 
                style={{ 
                  transform: `translate(${position.x}px, ${position.y}px)` 
                }}
                onMouseDown={handleMouseDown}
                ref={imageRef}
              >
                {currentFullSizePhoto.fileType.startsWith('image/') ? (
                  <img
                    src={currentFullSizePhoto.url}
                    alt={currentFullSizePhoto.fileName}
                    style={{ 
                      transform: `scale(${zoomLevel / 100})`,
                      transformOrigin: 'center',
                      transition: isDragging ? 'none' : 'transform 0.2s'
                    }}
                    className="max-h-full max-w-full"
                  />
                ) : (
                  <div className="bg-gray-800 text-white p-12 rounded-lg">
                    <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-center text-lg">{currentFullSizePhoto.fileName}</p>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => navigateToAdjacentPhoto('next')}
                className="absolute right-4 z-10 bg-black/30 hover:bg-black/50 rounded-full p-2 text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Footer information */}
            <div className="p-4 bg-gradient-to-t from-black/40 to-transparent">
              
            </div>
          </div>
        </div>
      )}
      {/* Submit Confirmation Popup */}
      {showSubmitPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 transform transition-all">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-center text-gray-800 mb-2">Successfully Submitted!</h3>
            <p className="text-gray-600 text-center mb-4">
              Your selection of {totalSelected} photo{totalSelected !== 1 ? 's' : ''} has been saved.
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => setShowSubmitPopup(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={viewSelectedPhotos}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-colors"
              >
                View Selected Photos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectedPhotos;
                          
                          